import type { Order } from '../features/orders/types/orders.types';
import type { ShopperPreferences } from '../features/shopper/types/shopper.types';
import { resolveCanonicalCustomerId, resolveCanonicalOrderId as resolveSharedCanonicalOrderId } from '../../shared/identity.js';
import { customerFacingStatus, normalizeCanonicalOrderStatus } from './contracts.js';
import { recordClientDiagnostic } from './clientDiagnostics.js';

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null;

const parseNumericLike = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(/[$,\s]/g, '');
  if (!normalized) return null;

  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : null;
};

const pickArray = (value: unknown): unknown[] | null => (Array.isArray(value) ? value : null);

export const resolveCanonicalOrderId = (order: unknown): string | null => (isRecord(order) ? resolveSharedCanonicalOrderId(order) : null);
export const resolveOrderIdentity = resolveCanonicalOrderId;

export const resolveOrdersPayload = (data: unknown): unknown[] => {
  if (!isRecord(data)) {
    return Array.isArray(data) ? data : [];
  }

  const directCandidates: unknown[] = [
    data.orders,
    isRecord(data.data) ? data.data.orders : null,
    data.data,
    data.items,
    data.results
  ];

  for (const candidate of directCandidates) {
    const arrayValue = pickArray(candidate);
    if (arrayValue) return arrayValue;
  }

  if (Array.isArray(data)) return data;
  return [];
};

const normalizeOrderTotal = (order: unknown) => {
  if (!isRecord(order)) return 0;

  const direct =
    order.total ??
    order.totalAmount ??
    order.amount ??
    order.totalDollars ??
    order.totalUSD ??
    order.totalValue;

  const directNumeric = parseNumericLike(direct);
  if (directNumeric !== null) return directNumeric;

  const cents =
    order.totalCents ??
    order.totalAmountCents ??
    order.amountCents ??
    order.amount_in_cents;

  const centsNumeric = parseNumericLike(cents);
  if (centsNumeric !== null) return centsNumeric / 100;

  return 0;
};

export const normalizeOrders = (input: unknown): Order[] => {
  const rawOrders = Array.isArray(input) ? input : resolveOrdersPayload(input);
  if (!Array.isArray(rawOrders) || rawOrders.length === 0) return [];

  const seenIds = new Set<string>();
  let skippedMissingId = 0;
  let skippedDuplicateId = 0;

  const normalized = rawOrders
    .map((order): Order | null => {
      if (!isRecord(order)) {
        skippedMissingId += 1;
        return null;
      }

      const canonicalId = resolveOrderIdentity(order);
      if (!canonicalId) {
        skippedMissingId += 1;
        return null;
      }

      if (seenIds.has(canonicalId)) {
        skippedDuplicateId += 1;
        return null;
      }

      seenIds.add(canonicalId);

      const customerId =
        resolveCanonicalCustomerId({
          customerId: order.customerId ?? order.customer_id,
          userId: order.userId,
          customer: isRecord(order.customer) ? order.customer.id : undefined
        }) ?? '';

      const internal = normalizeCanonicalOrderStatus(
        isRecord(order) && typeof order.internalStatus === 'string' && order.internalStatus.trim()
          ? order.internalStatus
          : order.status
      );

      return {
        ...order, // Spread existing properties
        id: canonicalId,
        amount: normalizeOrderTotal(order),
        customerId,
        status: internal,
        internalStatus: internal,
        customerState:
          isRecord(order) && typeof order.customerState === 'string' && String(order.customerState).trim()
            ? (order.customerState as Order['customerState'])
            : customerFacingStatus(internal),
        shopperPreferences: order.shopperPreferences as ShopperPreferences | undefined
      } as unknown as Order;
    })
    .filter((order): order is Order => Boolean(order));

  if (skippedMissingId > 0 || skippedDuplicateId > 0) {
    recordClientDiagnostic('Orders payload normalization skipped invalid entries', {
      received: rawOrders.length,
      normalized: normalized.length,
      skippedMissingId,
      skippedDuplicateId
    }, 'warning');
  }

  return normalized;
};
